import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

const SUPABASE_FUNCTIONS_URL = 'https://fyxrbfcnzlrjjbubjzth.supabase.co/functions/v1';

export interface CartItem {
  variantId: string;
  productTitle: string;
  variantTitle: string;
  price: number;
  quantity: number;
  imageUrl?: string;
  source: 'scooper' | 'refill';
}

interface CartContextValue {
  items: CartItem[];
  isOpen: boolean;
  isCheckingOut: boolean;
  addItem: (item: CartItem) => void;
  removeItem: (variantId: string) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  openCart: () => void;
  closeCart: () => void;
  checkout: () => Promise<void>;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const addItem = useCallback((newItem: CartItem) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.variantId === newItem.variantId);
      if (existing) {
        return prev.map((i) =>
          i.variantId === newItem.variantId
            ? { ...i, quantity: i.quantity + newItem.quantity }
            : i
        );
      }
      return [...prev, newItem];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((variantId: string) => {
    setItems((prev) => prev.filter((i) => i.variantId !== variantId));
  }, []);

  const updateQuantity = useCallback((variantId: string, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => i.variantId !== variantId));
    } else {
      setItems((prev) =>
        prev.map((i) => (i.variantId === variantId ? { ...i, quantity } : i))
      );
    }
  }, []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const checkout = useCallback(async () => {
    if (items.length === 0) return;
    setIsCheckingOut(true);
    try {
      const lines = items.map((item) => ({
        variantId: item.variantId,
        quantity: item.quantity,
      }));

      // Use shopify-products edge function for cart creation (supports all variants)
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/shopify-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'createCart', lines }),
      });
      const data = await res.json();
      const checkoutUrl = data?.data?.cartCreate?.cart?.checkoutUrl;
      if (checkoutUrl) {
        const returnUrl = `${window.location.origin}/thank-you`;
        const separator = checkoutUrl.includes('?') ? '&' : '?';
        window.location.href = `${checkoutUrl}${separator}return_url=${encodeURIComponent(returnUrl)}`;
      }
    } catch {
      // silently fail
    } finally {
      setIsCheckingOut(false);
    }
  }, [items]);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        isOpen,
        isCheckingOut,
        addItem,
        removeItem,
        updateQuantity,
        openCart,
        closeCart,
        checkout,
        totalItems,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
